import { useLocationParams } from '@/providers/BCRoutingContextProvider';
import {
  getCurrentCatalogV1StacklineSku,
  getCurrentCatalogV2RetailerSku,
  getCurrentStoreKind,
  isCatalogV2ProductContext,
} from '@/utils/bcProductMetadataUtils';
import { useEffect, useRef, useState } from 'react';

export interface CurrentProductContext {
  isCatalogV2: boolean;
  storeKind?: string;
  retailerSku?: string;
  stacklineSku?: string;
}

const POLL_INTERVAL_MS = 500;

const contextsAreEqual = (
  left: CurrentProductContext,
  right: CurrentProductContext
): boolean =>
  left.isCatalogV2 === right.isCatalogV2 &&
  left.storeKind === right.storeKind &&
  left.retailerSku === right.retailerSku &&
  left.stacklineSku === right.stacklineSku;

const readCurrentProductContext = (
  selectedVariantId?: string | null
): CurrentProductContext => {
  const isCatalogV2 = isCatalogV2ProductContext();
  if (isCatalogV2) {
    return {
      isCatalogV2,
      storeKind: getCurrentStoreKind(),
      retailerSku: getCurrentCatalogV2RetailerSku(selectedVariantId),
    };
  }

  return {
    isCatalogV2,
    stacklineSku: getCurrentCatalogV1StacklineSku(selectedVariantId),
  };
};

export const useCurrentProductContext = (): CurrentProductContext => {
  const searchParams = useLocationParams();
  const readSelectedVariantId = () =>
    new URLSearchParams(window.location.search).get('variant') ??
    searchParams?.get('variant');
  const [context, setContext] = useState(() =>
    readCurrentProductContext(readSelectedVariantId())
  );
  const previousContextRef = useRef<CurrentProductContext>(context);

  useEffect(() => {
    const syncContext = () => {
      const nextContext = readCurrentProductContext(readSelectedVariantId());
      if (!contextsAreEqual(nextContext, previousContextRef.current)) {
        previousContextRef.current = nextContext;
        setContext(nextContext);
      }
    };

    syncContext();

    const interval = setInterval(syncContext, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [searchParams]);

  return context;
};
